import { SignatureType, TDatabaseId, tg } from '@infotech-soe/metaschema';
import { isEmpty, merge } from 'lodash';

import {
  BuiltinCaptionType,
  CaptionType,
} from '../../../models/caption/Caption';
import {
  CategoryOpenMode,
  CategoryUI,
} from '../../../models/category/category';
import { DisableNodeStrategy } from '../../../models/category/categoryTree';
import { HookTypes } from '../../../models/category/HookTypes';
import { ConfirmationType } from '../../../models/confirmation/ConfirmationEntity';
import { DocLinksMode } from '../../../models/docLinks/docLinks';
import { CategoryTreeType } from '../../../services/category/view-model/CategoryViewModel';
import CommandService from '../../../services/commands/CommandService';
import CommandViewModel, {
  CommandListEntryType,
  CommandListEntryViewModel,
} from '../../../services/commands/CommandViewModel';
import ICommandService, {
  CommandExecuteSingleResult,
} from '../../../services/commands/ICommandService';
import { SignActionInitialArgs } from '../../../services/commands/types';
import { UserInteractorType } from '../../../services/commands/UserInteractor';
import { RefreshStrategy } from '../../../services/commands/wrap/CategoryCommand';
import { MoveRefreshStrategyBuilderArgs } from '../../../services/commands/wrap/MoveRecordCommand/refreshStrategyBuilder';
import ContextService from '../../../services/context/ContextService';
import IContextService from '../../../services/context/IContextService';
import { TreeFormFieldNames } from '../../../services/form/schema-factory/TreeFormSchemaFactory';
import { PredefinedForms } from '../../../services/form/view-model/FormCreateViewModel';
import { FormSourceType } from '../../../services/form/view-model/FormSource';
import IUUIDService from '../../../services/uid/IUUIDService';
import UUIDService from '../../../services/uid/UUIDService';
import { getCommand } from '../../../ui/components/dataset/utils/getCommand';
import { getProcessStateForCategory } from '../../selectors/actions';
import {
  getBalanceByDatasetId,
  getBalanceContentTab,
  getBalanceStateEntity,
} from '../../selectors/balance';
import { getCatalogsStateEntity } from '../../selectors/catalogs';
import { getCategoryState } from '../../selectors/category';
import { getDatasetCommandsScheduled } from '../../selectors/commands';
import {
  filterDatasetsForCategory,
  getDatasetCategoryId,
  getDatasetConditions,
  getDatasetSelectedIds,
  getDatasetState,
  isDatasetRoot,
} from '../../selectors/dataset';
import { getHierarchyStateEntity } from '../../selectors/hierarchy';
import {
  getSessionApplication,
  getSignFormState,
} from '../../selectors/session';
import actions from '../../slices/actions';
import balance, {
  BalanceAction,
  BalanceCategory,
  BalanceCheckReportsSpecsCategory,
} from '../../slices/balance';
import catalogs from '../../slices/catalogs';
import category from '../../slices/category';
import commands from '../../slices/commands';
import confirmation from '../../slices/confirmation';
import content from '../../slices/content';
import dataset from '../../slices/dataset';
import { DictionaryEffectType } from '../../slices/dictionary';
import docLinks from '../../slices/docLinks';
import form, { FormParentType } from '../../slices/form';
import graph from '../../slices/graph';
import hierarchy from '../../slices/hierarchy';
import pivot from '../../slices/pivot';
import review from '../../slices/review';
import rolePrivs from '../../slices/rolePrivs';
import session from '../../slices/session';
import workTable, { OpenWorkTablePostEffectType } from '../../slices/workTable';
import { dispatchBalanceEditValues } from '../../utils/dispatchBalanceEditValues';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const commandsListener = createAppListener();

const idService: IUUIDService = new UUIDService();
const contextService: IContextService = new ContextService();
const commandService: ICommandService = new CommandService();

const removeMatchedCommandsFromList = (
  commandsList: CommandViewModel[],
  matchCb: (model: CommandListEntryViewModel) => boolean
): CommandViewModel[] => {
  const result = commandsList.filter(model =>
    model.type === CommandListEntryType.COMMAND ? !matchCb(model) : true
  );

  for (const model of result) {
    if (
      model.type === CommandListEntryType.GROUP &&
      model.children.length > 0
    ) {
      model.children = removeMatchedCommandsFromList(model.children, matchCb);
    }
  }

  return result;
};

commandsListener.startListening(
  apiCall(
    {
      request: commands.get,
      success: commands.getSuccess,
      failure: commands.getFailure,
    },
    async ({ datasetId }, state) => {
      const categoryId = getDatasetCategoryId(state, datasetId);

      const application = getSessionApplication(state);
      const categoryState = getCategoryState(state, categoryId);
      const datasets = filterDatasetsForCategory(state, categoryId);
      const treeState =
        categoryState.treeType === CategoryTreeType.CATALOGS
          ? getCatalogsStateEntity(state, categoryId)
          : categoryState.treeType === CategoryTreeType.HIERARCHY
            ? getHierarchyStateEntity(state, categoryId)
            : undefined;

      const { commands: commandsList, keyMap } =
        await commandService.getCommands(
          categoryState,
          datasets,
          datasetId,
          application,
          treeState
        );

      return {
        datasetId,
        commands: isDatasetRoot(state, datasetId)
          ? commandsList
          : removeMatchedCommandsFromList(
              commandsList,
              model =>
                model.caption.type === CaptionType.BUILT_IN &&
                model.caption.caption === BuiltinCaptionType.MOVE
            ),
        keyMap,
        pending: getDatasetCommandsScheduled(state, datasetId),
      };
    }
  )
);

commandsListener.startListening(
  postDispatch(
    commands.getSuccess,
    (
      { datasetId, keyMap, commands: commandsList, pending },
      _state,
      dispatch
    ) => {
      dispatch(dataset.updateKeyMap({ datasetId, keyMap }));

      if (pending) {
        for (const commandName of pending) {
          if (getCommand(commandName, commandsList)?.available) {
            dispatch(commands.initiate({ datasetId, command: commandName }));
            break;
          }
        }
      }
    }
  )
);

commandsListener.startListening(
  apiCall(
    {
      request: commands.initiate,
      success: commands.initiateSuccess,
      failure: commands.initiateFailure,
    },
    async ({ datasetId, command, hook }, state, dispatch) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const datasets = filterDatasetsForCategory(state, categoryId);
      const application = getSessionApplication(state);
      if (!isEmpty(getProcessStateForCategory(state, categoryId))) {
        dispatch(actions.clearProcessState({ categoryId }));
      }
      const prescript = await commandService.initiateCommand(
        categoryState,
        datasets,
        datasetId,
        command,
        application,
        !!hook && command !== 'filter'
      );

      const context = await contextService.getContext(
        categoryState,
        state.dataset,
        datasetId
      );

      return { datasetId, command, hook, prescript, context };
    }
  )
);

commandsListener.startListening(
  apiCall(
    {
      request: commands.execute,
      success: commands.executeSuccess,
      failure: commands.executeFailure,
    },
    async (payload, state, dispatch) => {
      const { datasetId, command, args, hook } = payload;

      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const datasets = filterDatasetsForCategory(state, categoryId);
      const application = getSessionApplication(state);
      const result = await commandService.executeCommand(
        categoryState,
        datasets,
        datasetId,
        command,
        args,
        application,
        !!hook && command !== 'filter'
      );

      if (!result) {
        return payload;
      }

      if (result.refresh === RefreshStrategy.DYNAMIC && result.refreshBuilder) {
        const { activeRowId } = getDatasetState(state, datasetId);
        const treeCategoryId = getDatasetCategoryId(state, datasetId);
        const { treeType } = getCategoryState(state, treeCategoryId);
        const { checkedNodes: checkedTreeIds, activeId } =
          treeType === CategoryTreeType.CATALOGS
            ? getCatalogsStateEntity(state, treeCategoryId)
            : getHierarchyStateEntity(state, treeCategoryId);
        const targetTreeId: TDatabaseId = args[TreeFormFieldNames.TARGET_ID];

        if (
          !tg.isDbId(activeRowId) ||
          !tg.isDbId(targetTreeId) ||
          !tg.isDbId(activeId)
        ) {
          throw new Error("wrong store state, can't calc RefreshStrategy");
        }
        result.refresh = result.refreshBuilder({
          activeId,
          checkedTreeIds,
          targetTreeId,
        } as MoveRefreshStrategyBuilderArgs);
        delete result.refreshBuilder;
      }

      if (result.actionResult && result.actionName) {
        dispatch(
          actions.processMultistep({
            refreshRules: result.refreshRules,
            refresh: result.refresh,
            prevActionResult: result.actionResult,
            action: result.actionName,
            category: categoryState,
            datasets,
            datasetId,
            hook: !!hook,
          })
        );
        return { ...payload, isMidOfMultiStep: true as const };
      }

      if (result.results) {
        const selectedIds = getDatasetSelectedIds(state, datasetId);
        dispatch(
          dataset.changeSelectedIds({
            datasetId,
            selectedIds: selectedIds.filter(id => !result.results.has(id)),
          })
        );
      }

      const singleResult = result as CommandExecuteSingleResult;
      if (singleResult.actionType === 'Pivot') {
        dispatch(pivot.open({ dataset: datasetId, command, args }));
      } else if (singleResult.template && singleResult.data) {
        const { data, template, vars, events, parseDates } = singleResult;
        dispatch(
          review.open({
            data,
            template,
            variables: vars,
            events,
            parseDates,
          })
        );
      } else if (
        categoryState.source.category === BalanceCategory &&
        command === BalanceAction.EDIT_VALUES.name &&
        singleResult.data
      ) {
        dispatchBalanceEditValues(singleResult, datasetId, state, dispatch);
      }

      if (result.error) {
        if (result.results && result.results.size !== 0) {
          dispatch(dataset.update({ datasetId }));
        }
        throw result.error;
      }

      const afterSignAction = getSignFormState(state);
      if (command === afterSignAction?.command) {
        dispatch(session.closeSignForm());
      }

      return { ...payload, result };
    }
  )
);

commandsListener.startListening(
  postDispatch(
    commands.initiateSuccess,
    ({ datasetId, command, prescript, hook, context }, state, dispatch) => {
      switch (prescript.interactor.type) {
        case UserInteractorType.NONE:
          dispatch(
            commands.execute({
              datasetId,
              command,
              args: prescript.initialArgs,
              hook,
            })
          );
          break;

        case UserInteractorType.SHOW_CATEGORY: {
          const {
            source: { category: categoryName },
          } = state.dataset[datasetId];

          if (
            prescript.interactor.category === BalanceCheckReportsSpecsCategory
          ) {
            const balanceId = getBalanceByDatasetId(state, datasetId);
            const templateId = balanceId
              ? getBalanceStateEntity(state, balanceId).templateId
              : undefined;
            const inParams = {
              ...prescript.initialArgs.inParams,
              ...(templateId && { templateId }),
            };
            const existingTabId = getBalanceContentTab(
              state,
              prescript.interactor.category,
              inParams
            );
            if (existingTabId) {
              dispatch(content.activate({ id: existingTabId }));
              break;
            }
            dispatch(
              category.openDisplayMode({
                category: prescript.interactor.category,
                displayMode: prescript.interactor.displayMode,
                ui: { mode: CategoryOpenMode.CONTENT },
                inParams,
              })
            );
            break;
          }

          const openDisplayModeUIType: CategoryUI = {
            mode: CategoryOpenMode.DICTIONARY,
            effect: prescript.interactor.nextAction
              ? {
                  type: DictionaryEffectType.PROCESS_ACTION,
                  category: categoryName,
                  action: prescript.interactor.action,
                  dataset: prescript.interactor.dataset,
                  hook: !!hook,
                }
              : tg.hasBooleanProperty(
                    prescript.interactor.refreshRules,
                    'refreshOnClose'
                  ) && prescript.interactor.refreshRules.refreshOnClose
                ? {
                    type: DictionaryEffectType.REFRESH_ON_CLOSE,
                    dataset: prescript.interactor.dataset,
                    refresh: {
                      prevState: {},
                      refreshRules: prescript.interactor.refreshRules,
                    },
                  }
                : {
                    type: DictionaryEffectType.DO_NOTHING,
                  },
            multiSelect: prescript.interactor.multiSelect,
            hideTree: prescript.interactor.hideTree,
            fullHierarchy: prescript.interactor.fullHierarchy,
            hideCheckbox: prescript.interactor.hideCheckbox,
          };

          dispatch(
            category.openDisplayMode({
              category: prescript.interactor.category,
              displayMode: prescript.interactor.displayMode,
              ui: openDisplayModeUIType,
              inParams: prescript.initialArgs.inParams,
              nextAction: prescript.interactor.nextAction,
            })
          );

          break;
        }

        case UserInteractorType.DICTIONARY: {
          const categoryId = getDatasetCategoryId(state, datasetId);
          const categoryState = getCategoryState(state, categoryId);

          const openDisplayModeUIType: CategoryUI = {
            mode: CategoryOpenMode.DICTIONARY,
            effect: {
              type: DictionaryEffectType.EXECUTE_COMMAND,
              dataset: datasetId,
              command,
            },
            multiSelect: prescript.interactor.multiSelect,
          };

          if (prescript.initialArgs.fileLinks) {
            dispatch(
              category.openDisplayMode({
                category: 'FileLinks',
                displayMode: 'main',
                ui: openDisplayModeUIType,
                inParams: {
                  cndCategory: prescript.initialArgs.cndCategory,
                  cndRecordId: prescript.initialArgs.cndRecordId,
                },
              })
            );
          } else {
            dispatch(
              category.openDisplayMode({
                category: categoryState.source.category,
                displayMode: categoryState.source.displayMode,
                ui: openDisplayModeUIType,
              })
            );
          }

          break;
        }

        case UserInteractorType.CONFIRMATION:
          dispatch(
            confirmation.show({
              type: ConfirmationType.DATASET,
              messageType: prescript.interactor.message,
              dataset: datasetId,
              action: {
                command,
                args: prescript.initialArgs,
                hook: !!hook,
              },
              shortMessage: prescript.interactor.shortMessage,
              longMessage: prescript.interactor.longMessage,
              icon: prescript.interactor.icon,
            })
          );
          break;

        case UserInteractorType.DEFAULT_FORM:
          dispatch(
            form.create({
              type: FormSourceType.FORM,
              category: prescript.interactor.category,
              action: prescript.interactor.action,
              dataset: datasetId,
              command,
              args: prescript.initialArgs,
              title: prescript.interactor.caption,
              context,
              hook: !!hook,
            })
          );
          break;

        case UserInteractorType.PRINT_FORM:
          dispatch(
            form.create({
              type: FormSourceType.PRINT,
              category: prescript.interactor.category,
              action: prescript.interactor.action,
              dataset: datasetId,
              command,
              args: prescript.initialArgs,
              title: prescript.interactor.caption,
              context,
              hook: !!hook,
            })
          );
          break;

        case UserInteractorType.REVIEW_FORM:
        case UserInteractorType.PRINT_FORM_ON_CLIENT:
          dispatch(
            review.getData({ actionName: command, datasetId, prescript })
          );
          break;

        case UserInteractorType.CONDITION_FORM: {
          dispatch(
            form.create({
              type: FormSourceType.CONDITION,
              category: prescript.interactor.category,
              args: getDatasetConditions(state, datasetId).conditions,
              dataset: datasetId,
              formName: prescript.interactor.form,
              conditions: prescript.interactor.conditions,
              title: prescript.interactor.caption,
              command,
              hook: !!hook,
            })
          );
          break;
        }

        case UserInteractorType.SETTINGS_FORM:
          dispatch(dataset.openConfigForm({ datasetId }));
          break;

        case UserInteractorType.WORK_TABLE:
          dispatch(
            workTable.open({
              datasetId,
              tableType: prescript.interactor.tableType,
              effect: { type: OpenWorkTablePostEffectType.NOTHING },
            })
          );
          break;

        case UserInteractorType.DOC_LINKS_GRAPH:
          dispatch(
            graph.open({
              categoryName: context.get('Category'),
              docId: context.get('Id'),
            })
          );
          break;

        case UserInteractorType.MOVE_FORM: {
          const categoryId = getDatasetCategoryId(state, datasetId);
          const targetId = idService.generateId();
          const { treeType } = getCategoryState(state, categoryId);

          const catalogsStateEntity = getCatalogsStateEntity(state, categoryId);
          const hierarchyStateEntity = getHierarchyStateEntity(
            state,
            categoryId
          );

          const { datasets } = getCategoryState(state, categoryId);
          const rootDataset = Object.values(datasets).find(
            ({ parent }) => parent === null
          );
          const { activeRowId, rows, selectedIds, rowIdField, treeField } =
            state.dataset[rootDataset?.id ?? ''];

          const activeRow = rows.find(row => row[rowIdField] === activeRowId);

          if (treeType === CategoryTreeType.CATALOGS && treeField) {
            dispatch(catalogs.cloneTree({ catalogId: categoryId, targetId }));
            dispatch(
              catalogs.linkWithForm({ catalogId: targetId, formId: targetId })
            );

            const selectedRowsTreeIds = new Set<string>(
              selectedIds.map(
                id => rows.find(row => row[rowIdField] === id)?.[treeField]
              )
            );
            const disableIds =
              selectedIds.length > 0
                ? catalogsStateEntity.checkedNodes.isEmpty() ||
                  catalogsStateEntity.checkedNodes.size === 1
                  ? [activeRow?.[treeField]]
                  : selectedRowsTreeIds.size === 1
                    ? selectedRowsTreeIds
                    : []
                : [activeRow?.[treeField]];

            for (const nodeId of disableIds) {
              dispatch(catalogs.disableNode({ catalogId: targetId, nodeId }));
            }
          } else if (treeType === CategoryTreeType.HIERARCHY) {
            dispatch(
              hierarchy.cloneTree({ categoryId, targetId, formId: targetId })
            );

            const disableIds =
              selectedIds.length > 0 ? selectedIds : [activeRow?.[rowIdField]];

            for (const nodeId of disableIds) {
              dispatch(
                hierarchy.disableNode({
                  categoryId: targetId,
                  nodeId,
                  strategy: DisableNodeStrategy.RECURSIVE,
                })
              );
            }
          }

          const treeId =
            catalogsStateEntity?.activeId ||
            hierarchyStateEntity?.activeId ||
            null;

          dispatch(
            form.create({
              type: FormSourceType.PREDEFINED,
              subtype: PredefinedForms.TREE,
              category: categoryId,
              treeId: targetId,
              formId: targetId,
              command,
              parent: { type: FormParentType.DATASET, id: datasetId },
              values: { [TreeFormFieldNames.TARGET_ID]: treeId },
            })
          );
          break;
        }

        case UserInteractorType.ROLE_PRIVS:
          dispatch(rolePrivs.open(datasetId));
          break;

        case UserInteractorType.PIVOT_REPORT:
          dispatch(
            pivot.open({
              dataset: datasetId,
              command,
              args: prescript.initialArgs,
            })
          );
          break;

        case UserInteractorType.INPUT_DOCS:
          dispatch(
            docLinks.open({
              categoryName: context.get('Category'),
              docId: context.get('Id'),
              mode: DocLinksMode.IN,
            })
          );
          break;

        case UserInteractorType.OUTPUT_DOCS:
          dispatch(
            docLinks.open({
              categoryName: context.get('Category'),
              docId: context.get('Id'),
              mode: DocLinksMode.OUT,
            })
          );
          break;
        case UserInteractorType.SIGN_FORM: {
          if (
            (prescript.initialArgs as SignActionInitialArgs)
              .hasStoredCredentials
          ) {
            dispatch(commands.execute({ datasetId, command, args: {} }));
          } else {
            const signatureType: SignatureType =
              prescript.initialArgs.signatureType;
            dispatch(
              session.openSignForm({ datasetId, command, signatureType })
            );
          }
          break;
        }
      }
    }
  )
);

commandsListener.startListening(
  postDispatch(
    commands.executeSuccess,
    (
      { formId, result = {}, datasetId, hook, isMidOfMultiStep, command },
      state,
      dispatch
    ) => {
      if (hook === HookTypes.CLOSE) {
        return;
      }

      const categoryId = getDatasetCategoryId(state, datasetId);
      const { source } = getCategoryState(state, categoryId);
      const { refresh, refreshRules, results } = result;

      const { activeRowId, rowIdField } = getDatasetState(state, datasetId);
      let ids = results
        ? ([...results.keys()] as TDatabaseId[])
        : activeRowId
          ? [activeRowId]
          : [];

      if (refresh === RefreshStrategy.NEW && !results) {
        ids = [result[rowIdField]];
      }

      let inParams = result.inParams;
      if (
        command === 'filter' &&
        source.inParams?.linksGraph?.isIgnoreConditions
      ) {
        inParams = merge({}, source.inParams, inParams, {
          linksGraph: { isIgnoreConditions: false },
        });
      }

      if (inParams && !isEmpty(inParams)) {
        dispatch(category.updateInParams({ categoryId, inParams }));
      }

      if (formId) {
        dispatch(form.close({ formId }));
      }

      if (command === BuiltinCaptionType.MOVE) {
        dispatch(category.refreshTree({ categoryId }));
        dispatch(
          dataset.refreshWithStrategy({
            datasetId,
            strategy: refresh,
            ids,
          })
        );
      } else if (
        source.category === BalanceCategory &&
        getBalanceByDatasetId(state, datasetId) &&
        Object.values(BalanceAction).some(
          ({ name, needsRefresh }) => name === command && needsRefresh
        )
      ) {
        dispatch(balance.refresh({ datasetId }));
      } else if (hook) {
        if (!isMidOfMultiStep) {
          dispatch(
            category.load({
              categoryId: getDatasetCategoryId(state, datasetId),
            })
          );
        }
      } else if (refreshRules) {
        dispatch(
          category.refreshWithRules({
            datasetId,
            refreshRules,
            rowId: refresh === RefreshStrategy.NEW ? ids[0] : undefined,
          })
        );
      } else if (!isMidOfMultiStep) {
        dispatch(
          dataset.refreshWithStrategy({
            datasetId,
            strategy: refresh,
            ids,
          })
        );
      }
    }
  )
);

export default commandsListener.middleware;
